import type { CSSProperties, ReactNode } from 'react';
import estilos from './RejillaCampos.module.css';

interface RejillaCamposProps {
  children: ReactNode;
  /**
   * Ancho mínimo de cada columna. Por debajo de él la rejilla pasa sola a una
   * sola columna, así que no hacen falta consultas de medios en cada pantalla.
   */
  minimo?: string;
  className?: string;
}

/**
 * Rejilla para los campos de un formulario.
 *
 * Los formularios de la aplicación eran una columna única: en un diálogo de
 * seiscientos píxeles de ancho, un campo de «Puerto» ocupaba lo mismo que uno
 * de «Descripción», y un alta de seis campos obligaba a desplazarse para ver
 * el botón de guardar. Aquí los campos se reparten en las columnas que quepan
 * —dos en un diálogo normal, tres en uno ancho, una en el móvil— sin que cada
 * pantalla tenga que decir nada.
 *
 * Lo que necesita la fila entera —una casilla con su explicación, un texto
 * largo, un campo con un botón al lado— se envuelve en `CampoCompleto`.
 */
export function RejillaCampos({ children, minimo = '15rem', className }: RejillaCamposProps) {
  return (
    <div
      className={[estilos.rejilla, className].filter(Boolean).join(' ')}
      style={{ '--minimo-campo': minimo } as CSSProperties}
    >
      {children}
    </div>
  );
}

/** Ocupa todas las columnas de la rejilla. */
export function CampoCompleto({ children }: { children: ReactNode }) {
  return <div className={estilos.completo}>{children}</div>;
}

/**
 * Bloque con título dentro de un formulario largo.
 *
 * Seis campos seguidos se leen; quince, no. Agruparlos por asunto —«Conexión»,
 * «Aviso»— convierte una lista en un formulario que se entiende de un vistazo.
 * Dentro lleva su propia rejilla, así que sus campos también se reparten.
 */
export function GrupoCampos({
  titulo,
  minimo,
  children,
}: {
  titulo: string;
  minimo?: string;
  children: ReactNode;
}) {
  return (
    <section className={estilos.grupo}>
      <h3 className={estilos.grupoTitulo}>{titulo}</h3>
      <RejillaCampos minimo={minimo}>{children}</RejillaCampos>
    </section>
  );
}
